import { excludeChannels } from '@/pages/opinion/constants';
import { getCompetitorTopicKeywords } from '@/pages/opinion/services';
import { getFeedsKeyword } from '@/pages/opinion/services/feeds';
import { initialUpperCase } from '@/pages/opinion/utils';
import { mergeExcludeChannel } from '@op/components/FilterCommon/utils';
import { useState } from 'react';

export default function useBatchTopicRequest() {
  const [topicKeywordsLoading, setTopicKeywordsLoading] = useState(false);
  const [topicKeywordsApiError, setTopicKeywordsApiError] = useState(false);
  const [topicKeywords, setTopicKeywords] = useState<any>({});

  /**
   * 获取Topic/Keywords数据
   * @param params
   */
  const getKeywords = async (games: any[], otherParams: any) => {
    setTopicKeywordsLoading(true);
    const requestList = games.map(
      ({ edition_unified_id, id_type, entity_type, release_time }) => {
        return getFeedsKeyword(
          mergeExcludeChannel(
            {
              ...otherParams,
              sentiment_type: '',
              keyword_top_n: 8,
              keyword_sample_size: '500',
              edition_unified_id,
              id_type,
              entity_type,
              release_time,
            },
            excludeChannels,
          ),
        );
      },
    );

    Promise.all(requestList)
      .then((data) => {
        let keywords = games.reduce(
          (preObj, { edition_unified_id }: any, index: number) => {
            let keywordList: any = data?.[index] || [];
            keywordList = dealwithKeywordInfo(keywordList, (item: any) => ({
              ...item,
              men: item.cnt,
              sen: item.avg,
            }));
            return {
              ...preObj,
              [edition_unified_id]: keywordList,
            };
          },
          {},
        );

        setTopicKeywords(keywords);
      })
      .catch((error) => {
        setTopicKeywordsApiError(true);
        setTopicKeywords({ games: [] });
      })
      .finally(() => {
        setTopicKeywordsLoading(false);
      });
  };

  const dealwithKeywordInfo = (
    keywordList: {
      child: any;
      count: any;
      keyword: any;
      keyword_cn: any;
      keyword_en: any;
      sentiment_rating: any;
    }[],
    childFun: { (item: any): any; (item: any): { count: any; name: any } },
  ) => {
    return keywordList?.map(
      ({ child, count, keyword, keyword_cn, keyword_en, sentiment_rating }) => {
        return {
          name: keyword,
          name_en: keyword_en,
          name_cn: keyword_cn,
          kind:
            sentiment_rating < 2.5
              ? 'negative'
              : sentiment_rating > 3.5
              ? 'positive'
              : 'neutral',
          sentiment: sentiment_rating,
          mentions: count,
          child: child?.map(childFun),
        };
      },
    );
  };

  const dealwithRequestList = (
    requestList: any[],
    games: { edition_unified_id: any; id_type: any; entity_type: any }[],
  ) => {
    return Promise.all(requestList).then((data: Array<any>) => {
      let topicInfo = games.reduce(
        (preObj, { edition_unified_id }: any, index: number) => {
          let { list } = data?.[index] || { list: [] };

          let topicKeywordList = list?.map((item: any) => {
            let { keywords, topic, topic_cn, topic_en, mentions } = item || {};
            let keywordsInfo = dealwithKeywordInfo(
              keywords,
              (item: { cnt: any; keyword: any }) => ({
                count: item.cnt,
                name: item.keyword,
              }),
            );
            return {
              topic_name: initialUpperCase(topic),
              topic_name_cn: topic_cn,
              topic_name_en: topic_en,
              mentions: mentions,
              keywords: keywordsInfo,
            };
          });

          return {
            ...preObj,
            [edition_unified_id]: topicKeywordList,
          };
        },
        {},
      );
      return topicInfo;
    });
  };

  const [keywordsInPosTopic, setKeywordsInPosTopic] = useState({});
  const [keywordsInNegTopic, setKeywordsInNegTopic] = useState({});
  const getTopicKeywords = async (params: { [x: string]: any; games: any }) => {
    let { games, ...otherParams } = params;
    // 获取关键词部分数据
    getKeywords(games, otherParams);

    // 获取topic和关键词部分数据
    const positiveRequest: Array<any> = [];
    const negativeRequest: Array<any> = [];
    games.forEach(
      ({ edition_unified_id, id_type, entity_type, release_time }: any) => {
        let params = {
          ...otherParams,
          keyword_sample_size: '500',
          edition_unified_id,
          id_type,
          entity_type,
          page: 1,
          page_size: 2,
          sort_item: 'mentions',
          sort_model: 'desc',
          release_time,
        };
        positiveRequest.push(
          getCompetitorTopicKeywords(
            mergeExcludeChannel(
              { ...params, sentiment_type: 'positive' },
              excludeChannels,
            ),
          ),
        );
        negativeRequest.push(
          getCompetitorTopicKeywords(
            mergeExcludeChannel(
              { ...params, sentiment_type: 'negative' },
              excludeChannels,
            ),
          ),
        );
      },
    );

    const keywords_in_pos_topic = await dealwithRequestList(
      positiveRequest,
      games,
    );
    const keywords_in_neg_topic = await dealwithRequestList(
      negativeRequest,
      games,
    );
    setKeywordsInPosTopic(keywords_in_pos_topic);
    setKeywordsInNegTopic(keywords_in_neg_topic);
  };

  return {
    keywordsInPosTopic,
    keywordsInNegTopic,
    topicKeywordsLoading,
    topicKeywordsApiError,
    topicKeywords,
    getTopicKeywords,
    setTopicKeywords,
  };
}
